import React, { useRef, Suspense, useEffect, useMemo } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, useGLTF, PerspectiveCamera } from "@react-three/drei";
import { 
  Group, 
  Vector2, 
  Color, 
  MeshStandardMaterial,
} from "three";
import { EffectComposer, Bloom, ChromaticAberration, Vignette } from '@react-three/postprocessing';
import { BlendFunction } from 'postprocessing';
import styles from './BackgroundScene.module.css';

// Define types for materials and props
interface MaterialOptions {
  color?: string;
  metalness?: number;
  roughness?: number;
  emissiveIntensity?: number;
}

interface ModelProps {
  modelScale?: number;
  materials: MaterialOptions;
}

interface SceneProps {
  modelScale?: number;
}

// Default material properties
const DEFAULT_MATERIALS = {
  color: "#1F3D7A",  // Deep blue color
  metalness: 0.12,    // Slightly increased metalness for better reflections
  roughness: 0.37,    // Slightly decreased roughness for more shine
  emissiveIntensity: 0.22  // Slightly increased emissive intensity
};

/**
 * Rotating colored lights that orbit around the model in different radii
 */
const RotatingLights = React.memo(() => {
  const orbitalGroup1 = useRef<Group>(null);
  const orbitalGroup2 = useRef<Group>(null);
  
  // Animation frame counter for skipping frames
  const frameCount = useRef(0);
  const LIGHT_FRAME_SKIP = 3; // Only update light rotations every 4th frame
  
  useFrame(({ clock }) => {
    // Skip frames for light rotation calculations
    if (frameCount.current++ % LIGHT_FRAME_SKIP !== 0) return;
    
    const time = clock.getElapsedTime();
    
    // Create different orbital rotation patterns
    if (orbitalGroup1.current) {
      // First orbital ring rotates around Y axis
      orbitalGroup1.current.rotation.y = time * 0.2;
    }
    
    if (orbitalGroup2.current) {
      // Second orbital ring rotates around X axis with offset
      orbitalGroup2.current.rotation.x = time * 0.15;
      orbitalGroup2.current.rotation.z = Math.sin(time * 0.1) * 0.2;
    }
  });

  return (
    <>
      <ambientLight intensity={1.6} />
      <directionalLight position={[0, 5, 5]} intensity={2.2} color="#ffffff" />
      
      {/* First orbital ring - horizontal plane */}
      <group ref={orbitalGroup1}>
        <pointLight position={[3, 0, 0]} intensity={3.2} color="#147AFE" distance={15} decay={1.5} />
        <pointLight position={[-3, 0, 0]} intensity={3.2} color="#47CEFF" distance={15} decay={1.5} />
        <pointLight position={[0, 0, 3]} intensity={3.2} color="#147AFE" distance={15} decay={1.5} />
      </group>
      
      {/* Second orbital ring - vertical plane */}
      <group ref={orbitalGroup2}>
        <pointLight position={[2.5, 2.5, 0]} intensity={3.2} color="#147AFE" distance={12} decay={1.5} />
        <pointLight position={[-2.5, -2.5, 0]} intensity={3.2} color="#47CEFF" distance={12} decay={1.5} />
      </group>
    </>
  );
});

/**
 * Hook to create enhanced floating animation
 */
function useFloatingAnimation() {
  const rotation = useRef(new Vector2(0, 0));
  const frameCount = useRef(0);
  const FRAME_SKIP = 1; // Process every other frame, was too aggressive at 3
  
  // Animation parameters - more dramatic movements
  const params = useMemo(() => ({
    xAmplitude: 0.35,  // Increased from ~0.15
    yAmplitude: 0.3,   // Increased from ~0.12
    xSpeed: 0.4,       // Increased from 0.3
    ySpeed: 0.3,       // Increased from 0.25
    yPhaseOffset: 1.5, // Phase offset for y to create more complex motion
    secondaryAmp: 0.15,  // Secondary wave amplitude
    secondarySpeed: 0.8  // Secondary wave speed
  }), []);
  
  // Optimized animation frame with mild frame skipping
  useFrame(({ clock }) => {
    // Skip frames to reduce GPU load, but not too many
    if (frameCount.current++ % FRAME_SKIP !== 0) return;
    
    const time = clock.getElapsedTime();
    
    // Primary wave motion - enhanced amplitude
    const xWave = Math.sin(time * params.xSpeed) * params.xAmplitude;
    const yWave = Math.cos(time * params.ySpeed + params.yPhaseOffset) * params.yAmplitude;
    
    // Add secondary wave motion for more complexity
    const secondaryX = Math.sin(time * params.secondarySpeed * 1.3) * params.secondaryAmp;
    const secondaryY = Math.cos(time * params.secondarySpeed * 0.7) * params.secondaryAmp;
    
    // Combine waves for more complex motion
    rotation.current.x = xWave + secondaryX;
    rotation.current.y = yWave + secondaryY;
  });
  
  return rotation.current;
}

/**
 * Hook to handle model material and animations
 */
function useModelMaterial(materials: MaterialOptions) {
  // Create a shared material
  const baseColor = useMemo(() => new Color(materials.color || DEFAULT_MATERIALS.color), [materials.color]);
  const frameSkip = useRef(0);
  
  const modelMaterial = useMemo(() => {
    return new MeshStandardMaterial({
      color: baseColor,
      metalness: materials.metalness ?? DEFAULT_MATERIALS.metalness,
      roughness: materials.roughness ?? DEFAULT_MATERIALS.roughness,
      emissive: baseColor.clone().multiplyScalar(0.17),
      emissiveIntensity: (materials.emissiveIntensity ?? DEFAULT_MATERIALS.emissiveIntensity) * 1.25,
      flatShading: false,
    });
  }, [baseColor, materials.metalness, materials.roughness, materials.emissiveIntensity]);
  
  // Animate material properties with increased frame skipping
  useFrame(({ clock }) => {
    if (!modelMaterial) return;
    
    // Only update every 4th frame to reduce GPU load (was 3)
    if (frameSkip.current++ % 4 !== 0) return;
    
    const time = clock.getElapsedTime();
    
    // Cache values and check for significant changes to reduce needsUpdate calls
    const prevEmissive = modelMaterial.emissive.clone();
    const prevRoughness = modelMaterial.roughness;
    
    // Ensure color is maintained
    modelMaterial.color.copy(baseColor);
    
    // More dramatic emissive properties
    const r = 0.16 + 0.1 * Math.sin(time * 0.4);
    const g = 0.21 + 0.1 * Math.sin(time * 0.5);
    const b = 0.36 + 0.1 * Math.sin(time * 0.6);
    
    modelMaterial.emissive.setRGB(r, g, b);
    modelMaterial.emissiveIntensity = (DEFAULT_MATERIALS.emissiveIntensity * 1.25) * (Math.sin(time * 1.5) * 0.4 + 0.9);
    
    // More pronounced roughness animation
    modelMaterial.roughness = (materials.roughness ?? DEFAULT_MATERIALS.roughness) * (0.85 + Math.sin(time * 0.2) * 0.15);
    
    // Only set needsUpdate if there are significant changes - increased threshold for less updates
    const emissiveDiff = Math.abs(prevEmissive.r - r) + Math.abs(prevEmissive.g - g) + Math.abs(prevEmissive.b - b);
    const roughnessDiff = Math.abs(prevRoughness - modelMaterial.roughness);
    
    if (emissiveDiff > 0.015 || roughnessDiff > 0.01) {
      modelMaterial.needsUpdate = true;
    }
  });
  
  return modelMaterial;
}

/**
 * Main model component
 */
function Model({ modelScale = 1.5, materials }: ModelProps) {
  const { scene } = useGLTF("/assets/models/3d-models/rythm-logo.gltf");
  const logoRef = useRef<Group>(null);
  const rotation = useFloatingAnimation();
  const modelMaterial = useModelMaterial(materials);
  
  // Clone the scene to avoid mutating the cached original
  const clonedScene = useMemo(() => {
    if (!scene) return null;
    
    const clone = scene.clone();
    
    // Apply materials
    clone.traverse((child: any) => {
      if (child.isMesh && child.material) {
        // Apply shared material to reduce draw calls
        child.material = modelMaterial;
        
        // Disable unnecessary features
        child.castShadow = false;
        child.receiveShadow = false;
        child.frustumCulled = true;
      }
    });
    
    return clone;
  }, [scene, modelMaterial]);
  
  // Apply rotation from animation directly, no frame skipping here
  useFrame(() => {
    if (!logoRef.current) return;
    
    logoRef.current.rotation.x = rotation.x;
    logoRef.current.rotation.y = rotation.y;
  });

  return (
    <group ref={logoRef} scale={[modelScale, modelScale, modelScale]}>
      {clonedScene ? (
        <primitive object={clonedScene} />
      ) : (
        <mesh>
          <boxGeometry args={[1, 1, 1]} />
          <meshStandardMaterial color="#48F1B9" />
        </mesh>
      )}
    </group>
  );
}

/**
 * Camera controller
 */
const CameraController = React.memo(() => {
  const { camera } = useThree();
  
  useEffect(() => {
    camera.position.set(0, 0, 3);
    camera.updateProjectionMatrix();
  }, [camera]);
  
  return (
    <OrbitControls 
      enableZoom={false}
      enablePan={false}
      enableRotate={false}
      minDistance={2} 
      maxDistance={10}
      zoomSpeed={0.5}
    />
  );
});

/**
 * Post-processing effects
 */
const HighContrastEffects = () => (
  <EffectComposer enabled multisampling={0}> 
    <Bloom 
      intensity={1.2} 
      luminanceThreshold={0.35} 
      luminanceSmoothing={0.4} 
      mipmapBlur
    />
    <Vignette darkness={0.4} offset={0.4} />
    <ChromaticAberration 
      offset={new Vector2(0.0007, 0.0007)} 
      blendFunction={BlendFunction.LINEAR_LIGHT}
      radialModulation={false}
      modulationOffset={0.4}
    />
  </EffectComposer>
);

/**
 * Main Scene component
 */
function BackgroundScene({ modelScale = 1.5 }: SceneProps) {
  // Preload model and environment
  useEffect(() => {
    useGLTF.preload("/assets/models/3d-models/rythm-logo.gltf");
    return () => {
      // Clean up preloaded models
      useGLTF.clear("/assets/models/3d-models/rythm-logo.gltf");
    };
  }, []);

  // Prevent wheel events from affecting the 3D scene
  useEffect(() => {
    const handleWheel = (e: Event) => {
      // Prevent default wheel behavior on the 3D element
      e.preventDefault();
    };

    const sceneContainer = document.querySelector(`.${styles.sceneContainer}`);
    if (sceneContainer) {
      sceneContainer.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (sceneContainer) {
        sceneContainer.removeEventListener('wheel', handleWheel);
      }
    };
  }, []);

  return (
    <div className={styles.sceneContainer}>
      <Canvas
        gl={{ 
          powerPreference: "high-performance", 
          antialias: false, // Disable antialiasing to save GPU
          depth: false,
          alpha: true,
          precision: "lowp", // Lower precision for better performance
          stencil: false,
        }}
        dpr={[0.8, 1.2]} // Reduced device pixel ratio for better performance
        frameloop="always" // Always render to ensure smooth animation
        className={styles.canvas}
        onWheel={(e) => e.stopPropagation()}
        performance={{ min: 0.5 }} // Allow throttling under heavy load
      >
        <PerspectiveCamera makeDefault position={[0, 0, 3]} fov={55} />
        <Suspense fallback={null}>
          <Model modelScale={modelScale} materials={DEFAULT_MATERIALS} />
          <RotatingLights />
          <HighContrastEffects />
        </Suspense>
        <CameraController />
      </Canvas>
    </div>
  );
}

export default BackgroundScene; 